import React, {useState} from 'react'
import {useLocation} from 'react-router'
import {educationOptions, maritalStatusOptions, moneySpentOptions} from '../candidateFormOption'
import {Button} from '../../elements/Button'
import {Dropdown} from '../../elements/Dropdown'
import {TextArea} from '../../elements/TextArea'
import {Input} from '../../elements/Input'
import {ICandidate, ICandidateFormBirthDay} from '../../types/candidate'
import {validDate, isValidEmail} from '../../utils'
import {Checkbox} from '../../elements/Checkbox'
import CandidateConfirmModal from '../CandidateConfirmModal/CandidateConfirmModal'
import {FiCheck, FiX} from 'react-icons/fi'
import './CandidateForm.scss'
import styled from 'styled-components'
import {theme} from '../../theme'

interface IProps {
  candidate: ICandidate
  birthday: ICandidateFormBirthDay
  handleOnChange: (val: any, key: string) => void
  saveDetails: () => Promise<void>
  continueToAnswerPage: () => void
}

const CandidateForm: React.FC<IProps> = ({candidate, handleOnChange, saveDetails, continueToAnswerPage, birthday}) => {
  const {search} = useLocation()
  const isAdmin = search?.includes('admin_token')
  const [termsApproved, setTermsApproved] = useState(false)
  const [isSaveDetailsLoading, setIsSaveDetailsLoading] = useState(false)
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    customTitle: '',
    customMessage: ''
  })
  const isEmailValid = isValidEmail(candidate.email)

  const handleSaveDetails = async () => {
    try {
      setIsSaveDetailsLoading(true)
      await saveDetails()
      setIsSaveDetailsLoading(false)
      continueToAnswerPage()
    } catch (error) {
      setIsSaveDetailsLoading(false)
      setConfirmModalDetails(true, '', 'Tietojen tallennus epäonnistui. Yritä uudelleen.')
    }
  }

  const {county = {}, municipality = {}} = candidate.election_data || {}
  const hasElectionSelection = !!candidate.election_data
  const validBDay = validDate(candidate.birthday)
  const disableSave = !termsApproved || !validBDay || !hasElectionSelection || !candidate.image || !isEmailValid || !candidate.first_name

  const setConfirmModalDetails = (open: boolean, title?: string, message?: string) => {
    setConfirmModal({
      open,
      customTitle: title || '',
      customMessage: message || ''
    })
  }

  return (
    <>
      <CandidateConfirmModal
        isAdmin={isAdmin}
        setConfirmModal={setConfirmModalDetails}
        confirmModal={confirmModal}
        handleSave={handleSaveDetails}
        isLoading={isSaveDetailsLoading}
        candidateName={`${candidate.first_name} ${candidate.last_name}`}
        selectedAreas={{county, municipality}}
        requiredFields={{
          electionSelection: hasElectionSelection,
          municipalitySelection: !!candidate.hometown,
          firstName: !!candidate.first_name,
          birthday: !!validBDay,
          image: !!candidate.image,
          email: !!isEmailValid,
          terms: termsApproved
        }}
      />
      <div className="candidate-form">
        <p>TAUSTATIEDOT</p>
        <div className="candidate-form__birthday-container">
          <div className="candidate-form__birthday-container__text bolder">Syntymäaika:</div>
          <div className="candidate-form__birthday-container__text bold">
            {birthday.day}.{birthday.month}.{birthday.year}
          </div>
          <div className="candidate-form__birthday-container__text small">Vaalikoneessa näytetään vain ehdokkaan ikä</div>
        </div>
        <div className="candidate-form__email-input">
          <Input
            type={'email'}
            width={'432px'}
            label={
              <div className="candidate-form__email-label-container">
                * Sähköpostiosoite
                <EmailValidationMessage email={candidate.email} />
              </div>
            }
            onChange={e => {
              handleOnChange(e, 'email')
            }}
            value={candidate.email}
          />
        </div>
        <div className="candidate-form__sub-label small">
          Sähköpostia käytetään vain ehdokkaille tarkoitettua vaalikoneviestintää varten.
        </div>
        <Dropdown
          fieldKey={'id'}
          width={'320px'}
          label={'Koulutus'}
          placeHolder={'Valitse'}
          onSelect={e => handleOnChange(e, 'education')}
          options={educationOptions}
          displayField={'name'}
          value={candidate.education}
        />
        <Input
          type={'text'}
          width={'320px'}
          label={'Ammatti'}
          onChange={e => handleOnChange(e, 'occupation')}
          value={candidate.occupation}
        />
        <Dropdown
          fieldKey={'id'}
          width={'320px'}
          label={'Siviilisääty'}
          placeHolder={'Valitse'}
          onSelect={e => handleOnChange(e, 'marital_status')}
          options={maritalStatusOptions}
          displayField={'name'}
          value={candidate.marital_status}
          fullHeight={true}
        />
        <Input
          type={'number'}
          width={'140px'}
          label={'Lasten määrä'}
          onChange={e => Number(e) >= 0 && handleOnChange(Number(e), 'kids')}
          value={candidate.kids}
        />
        <TextArea
          width={'520px'}
          label={'Vaalilupaus, arvot'}
          onChange={e => handleOnChange(e, 'election_promise')}
          value={candidate.election_promise}
          rows={7}
          charLimit={2500}
        />
        <Dropdown
          fieldKey={'id'}
          width={'320px'}
          label={'Vaalibudjetti'}
          placeHolder={'Valitse'}
          onSelect={e => handleOnChange(e, 'money_spent')}
          options={moneySpentOptions}
          displayField={'name'}
          fullHeight={true}
          value={candidate.money_spent}
        />
        <TextArea
          width={'520px'}
          label={'Poliittiset luottamustoimet'}
          onChange={e => handleOnChange(e, 'political_responsibilities')}
          value={candidate.political_responsibilities}
          rows={7}
          charLimit={2500}
        />

        <div className="candidate-form__data-protection-container">
          <Checkbox
            label={
              <span>
                * Olen lukenut ja hyväksyn Alma Median{' '}
                <a className="candidate-form__red-link" href="https://www.almamedia.fi/tietosuoja" target="blank">
                  tietosuojakuvauksen
                </a>
              </span>
            }
            checked={termsApproved}
            onChange={() => setTermsApproved(!termsApproved)}
          />
        </div>
        <CustomButton
          onClick={() => setConfirmModalDetails(true, `${disableSave ? 'Tarkista' : 'Vahvista'} tiedot`)}
          height="40px"
          fontSize="18px"
          $disabled={disableSave}
        >
          Tallenna ja aloita vaalikone
        </CustomButton>
      </div>
    </>
  )
}

const EmailValidationMessage: React.FC<{email: string}> = ({email}) => {
  const isValid = isValidEmail(email)
  return (
    <div className="candidate-form__email-validation-icon">{isValid ? <FiCheck className="valid" /> : <FiX className="invalid" />}</div>
  )
}

export default CandidateForm

const CustomButton = styled(Button)<{$disabled: boolean}>`
  ${({$disabled}) => `background-color: ${$disabled ? theme.color.darkGray : theme.color.red}`}
`
